import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RmsModule } from '@rms';
import { RmsMessageService, WebService, lastValueFrom } from '@services';

import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';

interface TransferDestination {
  DestinationID: number;
  DestinationName: string;
  HoldingTankID?: number | null;
}

interface AvailableInventoryOption {
  LabelName: string;
  InventoryID: number;
  InventoryTypeName?: string;
  FinalPosition?: string;
  AvailableQty?: number;
  FinalDestinationWarehouseID?: number | null;
  FinalDestinationWarehouseName?: string | null;
}

interface InventoryToEdit {
  InventoryID: number;
  ParentInventoryID?: number | null;

  InventoryTypeName?: string;
  AvailableQty?: number;

  FinalDestinationWarehouseID?: number | null;
  FinalDestinationWarehouseName?: string | null;

  OriginWarehouseID?: number | null;
  OriginName?: string | null;
  OriginWarehouseName?: string | null;

  DestinationID?: number | null;
  DestinationName?: string | null;

  BagsOrdered?: number | null;
  BagsInbounded?: number | null;
  TransferBags?: number | null;

  HoldingTankID?: number | null;
  TransferStatus?: string | null;
}

export interface TransferPulldownResult {
  action: 'save';

  mode: 'create' | 'edit';

  // Child InventoryID during edit.
  // Selected source InventoryID during create.
  inventoryID: number;

  sourceInventoryID: number;

  originWarehouseID: number | null;
  previousDestinationID: number | null;
  destinationID: number;

  bagCount: number;
  holdingTankID: number | null;
}

@Component({
  selector: 'app-create-transfer',
  standalone: true,
  imports: [ RmsModule, CommonModule, FormsModule],
  templateUrl: './create-transfer.component.html',
  styleUrl: './create-transfer.component.scss'
})
export class CreateTransferComponent implements OnInit {
  warehouse = '';
  search = '';
  trackingFilter = 'all';
  readonly trackingFilters = [
    {value:'all',label:'All positions'}, {value:'instore',label:'In-store'},
    {value:'forward',label:'Forward · all'}, {value:'untracked',label:'Forward · no tracking'},
    {value:'etd',label:'Forward · ETD'}, {value:'afloat',label:'Forward · Afloat'},
    {value:'eta',label:'Forward · ETA'}, {value:'wta',label:'Forward · WTA'}
  ];
  trackingStage(item: AvailableInventoryOption): string {
    const position = String(item.FinalPosition || '').trim().toUpperCase();
    if (/^INSTORE\b/.test(position)) return 'instore';
    for (const stage of ['WTA','ETA','AFLOAT','ETD']) {
      if (new RegExp('^'+stage+'\\b').test(position)) return stage.toLowerCase();
    }
    return 'untracked';
  }
  matchesTracking(item: AvailableInventoryOption): boolean {
    const stage = this.trackingStage(item);
    return this.trackingFilter === 'all' || (this.trackingFilter === 'forward' ? stage !== 'instore' : stage === this.trackingFilter);
  }
  changeTrackingFilter(): void {
    this.selectedLookupInventoryID=null;
    this.newTransferBags=null;
    this.newTransferDestinationID=null;
  }
  saving = false;
  queue: (TransferPulldownResult & { coffee: string })[] = [];
  readonly warehouses = [{key:'NJ',name:'NJ'}, {key:'JAX',name:'JAX'}, {key:'WI',name:'WI'}];
  warehouseKey(id: unknown): string { return ({37:'NJ',43:'NJ',45:'JAX',31:'WI'} as Record<number,string>)[Number(id)] || String(id); }
  get batchMode(): boolean { return !this.isEditMode && !this.sourceLocked; }
  get filteredInventory(): AvailableInventoryOption[] {
    if (!this.warehouse) return [];
    const search = this.search.trim().toLowerCase();
    return this.availableInventoryOptions.filter(i => this.warehouseKey(i.FinalDestinationWarehouseID) === this.warehouse
      && this.matchesTracking(i)
      && Number(i.AvailableQty) > 0 && !this.queue.some(q => q.sourceInventoryID === i.InventoryID)
      && (!search || (i.InventoryID+' '+i.InventoryTypeName+' '+i.FinalPosition).toLowerCase().includes(search)))
      .sort((a,b) => (a.InventoryTypeName || '').localeCompare(b.InventoryTypeName || '') || a.InventoryID-b.InventoryID);
  }
  chooseWarehouse(key: string) { this.warehouse=key; this.search=''; this.selectedLookupInventoryID=null; this.newTransferBags=null; this.newTransferDestinationID=null; }
  chooseCoffee(item: AvailableInventoryOption) { this.selectedLookupInventoryID=item.InventoryID; this.newTransferBags=null; this.newTransferDestinationID=null; }
  removeQueued(index: number) { if (!this.saving) this.queue.splice(index,1); }
  async createAll() {
    if (this.saving || !this.queue.length) return;
    this.saving=true;
    try {
      await lastValueFrom(this.webService.getWebCall('inventory','coffee/inventory/transfer/stage-batch',{body:{Transfers:this.queue.map(q => ({
        InventoryID:q.sourceInventoryID,createChild:true,overrides:{BagsOrdered:q.bagCount,FinalDestinationWarehouseID:q.destinationID}
      }))}}));
      this.message.success(this.queue.length+' transfers created in the holding tank.');
      this.rmsPulldownRef.close({action:'batch-saved'});
    } catch(error:any) { this.message.error(error?.error?.error || 'Unable to create transfers. Your list is still here; please try again.'); }
    finally { this.saving=false; }
  }

  destinations: TransferDestination[] = [];

  availableInventoryOptions: AvailableInventoryOption[] = [];

  InventoryToEdit: InventoryToEdit | null = null;

  selectedLookupInventoryID: number | null = null;
  sourceLocked = false;
  newTransferBags: number | null = null;
  newTransferDestinationID: number | null = null;

  constructor(
    private message: RmsMessageService,
    private webService: WebService,
    private rmsPulldownRef: RmsPulldownRef,
    @Inject(RMS_PULLDOWN_DATA) private pulldownData: any
  ) {
    this.destinations = this.pulldownData?.destinations ?? [];

    this.availableInventoryOptions =
      this.pulldownData?.availableInventoryOptions ?? [];

    this.InventoryToEdit =
      this.pulldownData?.inventoryToEdit ?? null;
    this.selectedLookupInventoryID = this.toNullableNumber(this.pulldownData?.sourceInventoryID);
    this.sourceLocked = this.selectedLookupInventoryID !== null;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  get isEditMode(): boolean {
    return this.InventoryToEdit?.InventoryID != null;
  }

  get selectedLookupItem(): AvailableInventoryOption | null {
    if (this.selectedLookupInventoryID === null) {
      return null;
    }

    return this.availableInventoryOptions.find(
      option =>
        Number(option.InventoryID) ===
        Number(this.selectedLookupInventoryID)
    ) ?? null;
  }

  get originWarehouseID(): number | null {
    if (this.isEditMode) {
      return this.toNullableNumber(
        this.InventoryToEdit?.OriginWarehouseID
      );
    }

    return this.toNullableNumber(
      this.selectedLookupItem?.FinalDestinationWarehouseID
    );
  }

  get originWarehouseName(): string {
    if ([37,43,45,31].includes(Number(this.originWarehouseID))) return this.warehouseKey(this.originWarehouseID);
    if (this.isEditMode) {
      return (
        this.InventoryToEdit?.OriginWarehouseName ??
        this.InventoryToEdit?.OriginName ??
        'Unknown warehouse'
      );
    }

    return (
      this.selectedLookupItem?.FinalDestinationWarehouseName ??
      'Unknown warehouse'
    );
  }

  get transferDestinationOptions(): TransferDestination[] {
    const originID = this.originWarehouseID;

    if (originID === null) {
      return this.destinations;
    }

    return this.destinations.filter(
      destination =>
        this.warehouseKey(destination.DestinationID) !== this.warehouseKey(originID)
    );
  }

  get selectedNewDestinationName(): string | null {
    if (this.newTransferDestinationID === null) {
      return null;
    }

    return this.destinations.find(
      destination =>
        Number(destination.DestinationID) ===
        Number(this.newTransferDestinationID)
    ) ? this.warehouseKey(this.newTransferDestinationID) : null;
  }

  get saveButtonText(): string {
    return this.isEditMode
      ? 'Update Transfer'
      : this.batchMode ? 'Add to transfer list' : 'Create Transfer';
  }

  private initializeForm(): void {
    if (!this.InventoryToEdit) {
      return;
    }

    /*
     * The previous screen identifies the row by its child InventoryID.
     * Keep that child ID selected during edit mode.
     */
    this.selectedLookupInventoryID =
      this.toNullableNumber(
        this.InventoryToEdit.InventoryID
      );

    this.newTransferDestinationID =
      this.toNullableNumber(
        this.InventoryToEdit.DestinationID
      ) ??
      this.toNullableNumber(
        this.InventoryToEdit.FinalDestinationWarehouseID
      );

    this.newTransferBags =
      this.toNullableNumber(
        this.InventoryToEdit.TransferBags
      ) ??
      this.toNullableNumber(
        this.InventoryToEdit.BagsOrdered
      ) ??
      this.toNullableNumber(
        this.InventoryToEdit.BagsInbounded
      );

    this.validateInitializedDestination();
  }

  private validateInitializedDestination(): void {
    if (
      this.newTransferDestinationID !== null &&
      this.originWarehouseID !== null &&
      Number(this.newTransferDestinationID) ===
      Number(this.originWarehouseID)
    ) {
      this.newTransferDestinationID = null;

      this.message.error(
        'The destination must be different from the origin warehouse.'
      );
    }
  }

  private toNullableNumber(value: unknown): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numericValue = Number(value);

    return Number.isFinite(numericValue)
      ? numericValue
      : null;
  }

  close(): void {
    if (!this.saving) this.rmsPulldownRef.close();
  }

  save(): void {
    if (this.saving) return;
    if (this.batchMode && this.queue.length >= 100) { this.message.error('Create this batch before adding more than 100 transfers.'); return; }
    const selectedInventoryID =
      this.toNullableNumber(
        this.selectedLookupInventoryID
      );

    const destinationID =
      this.toNullableNumber(
        this.newTransferDestinationID
      );

    const bagCount =
      this.toNullableNumber(
        this.newTransferBags
      );

    if (selectedInventoryID === null) {
      this.message.error(
        'Select an inventory reference.'
      );
      return;
    }

    if (destinationID === null) {
      this.message.error(
        'Select a destination warehouse.'
      );
      return;
    }

    if (bagCount === null || !Number.isInteger(bagCount) || bagCount <= 0) {
      this.message.error(
        'Enter a whole number of bags greater than zero.'
      );
      return;
    }
    if (!this.isEditMode && (!this.selectedLookupItem || (this.selectedLookupItem.AvailableQty != null && bagCount > this.selectedLookupItem.AvailableQty))) {
      this.message.error('Select an available reference and enter a quantity within its available bags.');
      return;
    }

    if (
      this.originWarehouseID !== null &&
      Number(destinationID) ===
      Number(this.originWarehouseID)
    ) {
      this.message.error(
        'The destination must be different from the origin warehouse.'
      );
      return;
    }

    const selectedDestination =
      this.destinations.find(
        destination =>
          Number(destination.DestinationID) ===
          Number(destinationID)
      );

    if (!selectedDestination) {
      this.message.error(
        'The selected destination warehouse is not valid.'
      );
      return;
    }

    const inventoryID = this.isEditMode
      ? this.toNullableNumber(
        this.InventoryToEdit?.InventoryID
      )
      : selectedInventoryID;

    if (inventoryID === null) {
      this.message.error(
        'The transfer does not contain a valid inventory reference.'
      );
      return;
    }

    const sourceInventoryID =
      this.isEditMode
        ? (
          this.toNullableNumber(
            this.InventoryToEdit?.ParentInventoryID
          ) ?? inventoryID
        )
        : selectedInventoryID;

    const previousDestinationID =
      this.isEditMode
        ? (
          this.toNullableNumber(
            this.InventoryToEdit?.DestinationID
          ) ??
          this.toNullableNumber(
            this.InventoryToEdit
              ?.FinalDestinationWarehouseID
          )
        )
        : null;

    const result: TransferPulldownResult = {
      action: 'save',

      mode: this.isEditMode
        ? 'edit'
        : 'create',

      inventoryID,
      sourceInventoryID,

      originWarehouseID:
        this.originWarehouseID,

      previousDestinationID,
      destinationID,

      bagCount,

      holdingTankID:
        selectedDestination.HoldingTankID ?? null
    };

    if (this.batchMode) {
      if (this.queue.some(q => q.sourceInventoryID === result.sourceInventoryID)) return;
      this.queue.push({...result,coffee:this.selectedLookupItem?.InventoryTypeName || ''});
      this.selectedLookupInventoryID=null;
      this.newTransferBags=null;
    } else this.rmsPulldownRef.close(result);
  }
}
